import type { RunResponse } from '@malaberg/shared';
/**
 * useRuns hook.
 *
 * Fetches the paginated list of runs from the API.
 * - Normal History is scoped to the logged-in user for all roles.
 * - Admin / system_owner callers may pass filters.userId for explicit inspection.
 *
 * Pagination:
 *   - Page size: 10 items (offset-based, page-replace — not append).
 *   - `refetch`: re-runs the current page.
 *   - `goToPage(n)`: jump to page n (1-indexed).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { runsApi } from '../api/endpoints/runs';
import { useAuthStore } from '../state/auth-store';
import { useRunStreamStore } from '../state/run-stream-store';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseRunsFilters {
  product?: string;
  status?: string;
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
  q?: string;
}

export interface UseRunsResult {
  runs: RunResponse[];
  total: number;
  page: number;
  totalPages: number;
  limit: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  goToPage: (page: number) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

const LIMIT = 10;

export function useRuns(filters: UseRunsFilters = {}): UseRunsResult {
  const user = useAuthStore((s) => s.user);

  const [runs, setRuns] = useState<RunResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track a "fetch generation" so stale responses from cancelled fetches don't
  // overwrite newer state when navigating quickly.
  const generationRef = useRef(0);

  const doFetch = useCallback(
    async (fetchOffset: number) => {
      if (!user) return;

      const generation = ++generationRef.current;
      setLoading(true);

      const explicitUserId =
        user.role === 'admin' || user.role === 'system_owner' ? filters.userId : undefined;
      const userId = explicitUserId ?? user.id;

      try {
        const response = await runsApi.list({
          limit: LIMIT,
          offset: fetchOffset,
          userId,
          product: filters.product,
          status: filters.status,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          q: filters.q,
        });

        if (generation !== generationRef.current) return;

        const { items, total: newTotal } = response as {
          ok: true;
          items: RunResponse[];
          total: number;
          limit: number;
          offset: number;
        };

        setRuns(items);
        setTotal(newTotal);
        setOffset(fetchOffset);
        setError(null);
      } catch (err) {
        if (generation !== generationRef.current) return;
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (generation === generationRef.current) {
          setLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      user?.id,
      user?.role,
      filters.product,
      filters.status,
      filters.userId,
      filters.dateFrom,
      filters.dateTo,
      filters.q,
    ]
  );

  useEffect(() => {
    void doFetch(0);
  }, [doFetch]);

  // Auto-refetch the current page when a generation is submitted / completes
  // / errors, so the sidebar reflects new entries without manual refresh.
  const runsListVersion = useRunStreamStore((s) => s.runsListVersion);
  useEffect(() => {
    if (runsListVersion === 0) return;
    void doFetch(offset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runsListVersion]);

  const refetch = useCallback(() => {
    void doFetch(offset);
  }, [doFetch, offset]);

  const goToPage = useCallback(
    (newPage: number) => {
      const safePage = Math.max(1, newPage);
      void doFetch((safePage - 1) * LIMIT);
    },
    [doFetch]
  );

  const page = Math.floor(offset / LIMIT) + 1;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const hasMore = page < totalPages;

  return {
    runs,
    total,
    page,
    totalPages,
    limit: LIMIT,
    hasMore,
    loading,
    error,
    refetch,
    goToPage,
  };
}
